import { hideBackdrop, moveBackdrop } from '@tesla/design-system';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef } from 'react';
import { Tab } from './tab.js';
/**  If the variant is underline, wrap the children in a div with the underline inner class */
const UnderlineVariantWrapper = ({ children, variant }) => {
    return variant === 'underline' ? (React.createElement("div", { className: "tds-tab-list--underline--inner" }, children)) : (React.createElement(React.Fragment, null, children));
};
export const TabList = ({ align, animated, ariaLabel = 'Tab List', children, className, fullWidth, onTabChange, selected, tabs, variant, ...rest }) => {
    const firstRender = useRef(false);
    const ref = useRef(null);
    /**
     * selectTab
     * 1. get the currently selected tab
     * 2. move the backdrop to match that tab's dimensions/position
     */
    const selectTab = useCallback(() => {
        if (!ref.current)
            return;
        const selectedTab = ref.current.querySelector('.tds-tab[aria-selected="true"]');
        // If the selected tab is no longer in the list hide it
        if (tabs && !(tabs === null || tabs === void 0 ? void 0 : tabs.some((tab) => tab.id === (selectedTab === null || selectedTab === void 0 ? void 0 : selectedTab.id)))) {
            hideBackdrop(ref.current);
            return;
        }
        if (ref.current) {
            moveBackdrop(ref.current, selectedTab, firstRender.current);
        }
        // On the first render, don't allow transitions
        // otherwise the backdrop would animate into it's initial place
        if (!firstRender.current)
            firstRender.current = true;
    }, [tabs, ref]);
    /** Update the tabs position on resize */
    useEffect(() => {
        window.addEventListener('resize', selectTab);
        /** Select the tab again in case the width of the tab has changed after page load.
         *  For example, if the font size changes due to font-display: swap;
         */
        window.addEventListener('load', selectTab);
        return () => {
            window.removeEventListener('resize', selectTab);
            window.removeEventListener('load', selectTab);
        };
    }, [selectTab, tabs, children]);
    // Update the tab's position on selection change
    useEffect(() => {
        selectTab();
    }, [children, tabs, selected, selectTab]);
    // Don't render the shell if there's nothing within
    if ((!children && !tabs) || (!children && (tabs === null || tabs === void 0 ? void 0 : tabs.length) === 0)) {
        return null;
    }
    return (React.createElement("div", { "aria-label": ariaLabel, "aria-orientation": variant === 'vertical' || variant === 'legacy-vertical' ? 'vertical' : 'horizontal', className: cx('tds-tab-list', {
            'tds-tab-list--animated': animated,
            'tds-tab-list--full-width': fullWidth,
            [`tds-tab-list--align-${align}`]: align,
            [`tds-tab-list--${variant}`]: variant,
        }, className), ref: ref, role: "tablist", ...rest },
        React.createElement(UnderlineVariantWrapper, { variant: variant },
            animated && React.createElement("div", { className: "tds--animated-backdrop" }),
            tabs
                ? tabs.map(({ id, text, ...tabProps }) => (React.createElement(Tab, { id: id, key: id, onChange: onTabChange, selected: selected === id, ...tabProps }, text)))
                : children)));
};
